/**
 * **UI 的量纲** —— 「UI 大小」这个无量纲倍率落在这里。
 *
 * ## 为什么只给一个倍率，不给一套尺寸表
 *
 * 评审的原话：「UI大小是一个**无量纲的倍率**，各类UI组件自己决定该如何根据
 * 这个倍率修改尺寸。」
 *
 * 这条要求是对的，而且和 `visual-design.md` 里那条硬性反模式是同一件事：
 *
 * > ❌ **裸的像素值** —— 这是屏幕语言里最隐蔽的一条。
 * > **一切长度相对一个锚。**
 *
 * 一个按钮的"合适尺寸"不是 `高 32px`，而是「**能装下它自己那行字，再加一圈
 * 可以按的边**」。字号变了、字变了、倍率变了，这个尺寸**跟着变**才对。
 * 所以这里**不给** `BUTTON_HEIGHT` 这种常量 —— 一旦给了，所有组件都会去读它，
 * 于是"每个组件自己决定"就退化成了"全局一个写死的数"。
 *
 * 这里只给三样东西：
 * - 倍率的**合法范围与默认值**
 * - 一个把倍率夹到范围内的函数
 * - 两个**可选的**换算助手（线性 / 次线性），组件想用就用，不用也没关系
 *
 * ## 和别的"尺寸来源"的关系
 *
 * | 锚 | 谁在用 | 例子 |
 * | --- | --- | --- |
 * | **牌高** | 手牌、卡面、桌面 | `hand/layout.ts` 的一切 |
 * | **画布** | 版面、背景、棋盘 | `LOGICAL_WIDTH` / `LOGICAL_HEIGHT` |
 * | **UI 倍率** | 菜单、HUD、面板 | 这个文件 |
 *
 * ⚠️ UI 倍率**不缩放牌桌**。牌桌要整屏自适应，再乘一个倍率会溢出画布 ——
 * 这是评审明确选的（"只放大菜单/HUD/文字"）。
 */

/**
 * UI 倍率的范围。
 *
 * 下限 0.7：再小的话中文正文会低于 9px，读起来是种折磨。
 * 上限 2.0：再大的话一个面板就占满整张画布，菜单失去版面感。
 */
export const UI_SCALE_MIN = 0.7;
export const UI_SCALE_MAX = 2;

/** 默认倍率。1 = 设计时的样子 */
export const DEFAULT_UI_SCALE = 1;

/** 把任意输入夹进合法范围。`NaN` / `Infinity` 一律回默认值 */
export function clampUiScale(value: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return DEFAULT_UI_SCALE;
  if (value < UI_SCALE_MIN) return UI_SCALE_MIN;
  if (value > UI_SCALE_MAX) return UI_SCALE_MAX;
  return value;
}

/**
 * **线性**换算：`base` 在设计尺寸下的值 → 当前倍率下的值。
 *
 * 适合：间距、边框粗细、图标方块、滑块轨道的厚度 —— 这些东西
 * 按比例放大看起来就是"同一张版面印大了一点"，是对的。
 */
export function scaled(base: number, scale: number): number {
  return base * clampUiScale(scale);
}

/**
 * **次线性**换算：字号用这个。
 *
 * 为什么字号不能线性跟着走：倍率区间是 0.7–2.0，也就是 2.86 倍跨度。
 * 正文在 1.0 时 14px，线性到 2.0 就是 28px —— 一行只放得下十来个字，
 * 版面会散架；而线性到 0.7 是 9.8px，中文已经快糊了。
 * 取 **0.75 次幂**，两端分别落在 ~11.5px 和 ~23.6px，都还能读。
 *
 * ⚠️ 这是**组件自己的判断**，不是全局规定。一个只想变大一点点的角标
 * 完全可以继续用 `scaled()`。两个函数都摆在这儿，用哪个是组件的选择。
 */
export function scaledFont(base: number, scale: number): number {
  const k = clampUiScale(scale);
  return base * Math.pow(k, 0.75);
}

/**
 * 把字号**取整到半像素**。
 *
 * 字体的 hinting 在半像素上比在 13.37px 这种值上稳得多，
 * 而 `Text` 每次字号变化都要重排版 —— 取整还能让缓存命中率高一点。
 */
export function roundFont(size: number): number {
  return Math.round(size * 2) / 2;
}
